/**
 * Product multi-image slots + SKU WebP Storage naming contract.
 * Product Management owns ImageURLs writes; Imagizer uploads Storage objects only
 * (optional batch apply uses the same normalize helpers).
 */

export const PRODUCT_IMAGE_MAX_SLOTS = 6;
export const PRODUCT_IMAGES_PREFIX = 'product-images';
export const PRODUCT_IMAGE_WEBP_EXT = 'webp';

/**
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeImageUrlList(value) {
    const raw = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];
    const seen = new Set();
    const out = [];
    for (const item of raw) {
        const url = String(item || '').trim();
        if (!url || seen.has(url)) continue;
        seen.add(url);
        out.push(url);
        if (out.length >= PRODUCT_IMAGE_MAX_SLOTS) break;
    }
    return out;
}

/**
 * Sync ImageURLs + legacy Image URL on a product payload.
 * Primary image is always ImageURLs[0] (or blank when empty).
 *
 * @param {object} doc
 * @returns {object}
 */
export function syncProductImageFields(doc = {}) {
    const next = { ...(doc || {}) };
    const fromArray = Array.isArray(next.ImageURLs) ? next.ImageURLs : [];
    const legacy = next['Image URL'] || next.imageUrl || '';
    // Prefer compacted ImageURLs; only then fall back to legacy Image URL
    // (blank-only slot arrays must not wipe a still-valid primary).
    let urls = normalizeImageUrlList(fromArray);
    if (!urls.length && legacy) {
        urls = normalizeImageUrlList([legacy]);
    }
    next.ImageURLs = urls;
    next['Image URL'] = urls[0] || '';
    if (Object.prototype.hasOwnProperty.call(next, 'imageUrl')) {
        next.imageUrl = next['Image URL'];
    }
    return next;
}

/**
 * Pad / trim slot list to an explicit count (empty string = empty slot).
 * @param {string[]} urls
 * @param {number} slotCount
 * @returns {string[]}
 */
export function resizeImageSlots(urls = [], slotCount = 1) {
    const n = Math.max(1, Math.min(PRODUCT_IMAGE_MAX_SLOTS, Number(slotCount) || 1));
    const base = normalizeImageUrlList(urls);
    const slots = base.slice(0, n);
    while (slots.length < n) slots.push('');
    return slots;
}

/**
 * Compact slots (drop blanks) then sync fields for Firestore write.
 * @param {object} doc
 * @returns {object}
 */
export function prepareProductImageFieldsForWrite(doc = {}) {
    const synced = syncProductImageFields(doc);
    const urls = normalizeImageUrlList(synced.ImageURLs);
    return {
        ...synced,
        ImageURLs: urls,
        'Image URL': urls[0] || '',
    };
}

/**
 * Storage object path for a SKU image slot (0-based).
 * Slot 0 → product-images/{SKU}.webp
 * Slot 1 → product-images/{SKU}_2.webp
 *
 * @param {string} sku
 * @param {number} [slotIndex=0]
 * @returns {string|null}
 */
export function buildSkuWebpStoragePath(sku, slotIndex = 0) {
    const code = String(sku || '')
        .trim()
        .replace(/[^\w.-]/g, '_');
    if (!code) return null;
    const idx = Math.max(0, Math.min(PRODUCT_IMAGE_MAX_SLOTS - 1, Number(slotIndex) || 0));
    if (idx === 0) return `${PRODUCT_IMAGES_PREFIX}/${code}.${PRODUCT_IMAGE_WEBP_EXT}`;
    return `${PRODUCT_IMAGES_PREFIX}/${code}_${idx + 1}.${PRODUCT_IMAGE_WEBP_EXT}`;
}

/**
 * @param {string} fullPath
 * @returns {{ sku: string, slotIndex: number }|null}
 */
export function parseSkuWebpStoragePath(fullPath) {
    const path = String(fullPath || '')
        .replace(/^\/+/, '')
        .trim();
    const prefix = `${PRODUCT_IMAGES_PREFIX}/`;
    if (!path.toLowerCase().startsWith(prefix)) return null;
    const name = path.slice(prefix.length);
    return parseSkuAndSlotFromStem(name, true);
}

/**
 * Parse a shoot filename into SKU + slot (extension ignored).
 * Examples: CH-NDSM-0080-SLC.jpg → slot 0; CH-NDSM-0080-SLC_2.png → slot 1
 *
 * @param {string} filename
 * @returns {{ sku: string, slotIndex: number }|null}
 */
export function parseShootImageFilename(filename) {
    const base = String(filename || '')
        .trim()
        .split(/[/\\]/)
        .pop();
    if (!base) return null;
    return parseSkuAndSlotFromStem(base, false);
}

/**
 * @param {string} fileNameOrObjectName
 * @param {boolean} requireWebpExt
 * @returns {{ sku: string, slotIndex: number }|null}
 */
function parseSkuAndSlotFromStem(fileNameOrObjectName, requireWebpExt) {
    let stem = String(fileNameOrObjectName || '').trim();
    if (!stem) return null;
    if (requireWebpExt) {
        if (!/\.webp$/i.test(stem)) return null;
        stem = stem.replace(/\.webp$/i, '');
    } else {
        stem = stem.replace(/\.[^.]+$/, '');
    }
    // Slot suffix _2 … _6 only (primary has no suffix). Match suffix first so SKUs may contain hyphens.
    const slotMatch = stem.match(/^(.*)_([2-6])$/);
    if (slotMatch) {
        const sku = String(slotMatch[1] || '').trim();
        const slotIndex = Number(slotMatch[2]) - 1;
        if (!looksLikeProductSku(sku)) return null;
        return { sku, slotIndex };
    }
    if (!looksLikeProductSku(stem)) return null;
    // Reject bare trailing _digit that is not 2-6 (invalid slot)
    if (/_\d+$/.test(stem)) return null;
    return { sku: stem, slotIndex: 0 };
}

/** London Smoke & Cure catalogue codes use hyphenated segments (e.g. CH-NDSM-0080-SLC). */
function looksLikeProductSku(sku) {
    const s = String(sku || '').trim();
    if (!s || s.length > 128) return false;
    if (!/^[A-Za-z0-9]/.test(s)) return false;
    if (/\s/.test(s)) return false;
    const hyphens = (s.match(/-/g) || []).length;
    return hyphens >= 2;
}

/**
 * Reorder ImageURLs so `fromIndex` moves to `toIndex`.
 * @param {string[]} urls
 * @param {number} fromIndex
 * @param {number} toIndex
 */
export function reorderImageSlots(urls = [], fromIndex, toIndex) {
    const list = [...(urls || [])];
    if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= list.length ||
        toIndex >= list.length ||
        fromIndex === toIndex
    ) {
        return list;
    }
    const [item] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, item);
    return list;
}

/**
 * Set one slot URL (keeps empties for editor UI until write).
 * Clamps slotIndex into 0..PRODUCT_IMAGE_MAX_SLOTS-1 so the list never exceeds max 6.
 * @param {string[]} urls
 * @param {number} slotIndex
 * @param {string} url
 */
export function setImageSlotUrl(urls = [], slotIndex, url) {
    const idx = Math.max(0, Math.min(PRODUCT_IMAGE_MAX_SLOTS - 1, Number(slotIndex) || 0));
    const list = resizeImageSlots(urls, Math.max(urls.length || 1, idx + 1));
    list[idx] = String(url || '').trim();
    return list.slice(0, PRODUCT_IMAGE_MAX_SLOTS);
}

/**
 * Preserve empty slots for the PM editor (do not compact).
 * Primary is the first non-empty URL.
 * @param {string[]} urls
 * @param {number} [slotCount]
 * @returns {{ ImageURLs: string[], 'Image URL': string }}
 */
export function preserveEditorImageSlots(urls = [], slotCount) {
    const count = Math.max(
        1,
        Math.min(
            PRODUCT_IMAGE_MAX_SLOTS,
            Number.isFinite(Number(slotCount)) ? Number(slotCount) : urls.length || 1
        )
    );
    const ImageURLs = resizeImageSlots(urls, count).map((u) => String(u || '').trim());
    const primary = ImageURLs.find(Boolean) || '';
    return { ImageURLs, 'Image URL': primary };
}
